import React, { useState, useEffect } from 'react';
import './ShiftManagement.css';
import authService from '../services/auth.service';
import apiService from '../services/api.service';
import EmployeeManagement from './EmployeeManagement';
import ShiftTypeManagement from './ShiftTypeManagement';
import AvailabilityManagement from './AvailabilityManagement';
import EmployeePaymentManagement from './EmployeePaymentManagement';
import CheckInManagement from './CheckInManagement';

// Time conversion utilities
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const pad2 = (n) => n.toString().padStart(2, '0');

const toYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const fromYMD = (ymd) => {
  if (!ymd) return new Date();
  const [y, m, d] = ymd.split('-').map(Number);
  // Use midday to avoid timezone/DST edge-cases when converting to/from strings.
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
};

const timeStringToMinutes = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

const formatDate = (dateString) => {
  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('tr-TR', options);
};

const weekDayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const ShiftManagement = () => {
  const [currentView, setCurrentView] = useState('weekly'); // 'weekly', 'employees', 'shifttypes', 'availabilities', 'payments', 'checkins'
  const [weeklyShifts, setWeeklyShifts] = useState({});
  const [employees, setEmployees] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => toYMD(getMonday(new Date())));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [currentShift, setCurrentShift] = useState({
    date: '',
    employeeId: '',
    shiftTypeId: '',
    startTime: '',
    endTime: '',
    isCustomTime: false,
    notes: ''
  });
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  
  // Get Monday of current week
  function getMonday(d) {
    d = new Date(d);
    d.setHours(12, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(d.setDate(diff));
  }
  
  // Generate dates for the current week
  const getWeekDates = () => {
    const dates = [];
    const monday = fromYMD(currentWeekStart);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push({
        date: toYMD(date),
        dayName: weekDayNames[i],
        displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'numeric' })
      });
    }
    
    return dates;
  };
  
  // Get previous week
  const getPreviousWeek = () => {
    const monday = fromYMD(currentWeekStart);
    monday.setDate(monday.getDate() - 7);
    setCurrentWeekStart(toYMD(monday));
  };
  
  // Get next week
  const getNextWeek = () => {
    const monday = fromYMD(currentWeekStart);
    monday.setDate(monday.getDate() + 7);
    setCurrentWeekStart(toYMD(monday));
  };
  
  // Go to current week
  const goToCurrentWeek = () => {
    setCurrentWeekStart(toYMD(getMonday(new Date())));
  };
  
  // Fetch weekly shifts
  const fetchWeeklyShifts = async () => {
    setLoading(true);
    try {
      const response = await apiService.getWeeklyShifts(currentWeekStart);
      setWeeklyShifts(response.data.shifts);
      setError(null);
    } catch (err) {
      console.error('Error fetching weekly shifts:', err);
      setError('Haftalık mesailer yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch employees
  const fetchEmployees = async () => {
    try {
      // Only get active employees for shift assignments
      const response = await apiService.getEmployees({ isActive: true });
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Çalışanlar yüklenirken bir hata oluştu: ' + err.message);
    }
  };
  
  // Fetch shift types
  const fetchShiftTypes = async () => {
    try {
      const response = await apiService.getShiftTypes();
      setShiftTypes(response.data);
    } catch (err) {
      console.error('Error fetching shift types:', err);
      setError('Vardiya türleri yüklenirken bir hata oluştu: ' + err.message);
    }
  };
  
  // Load data when component mounts or week changes
  useEffect(() => {
    if (currentView === 'weekly') {
      fetchWeeklyShifts();
    }
    fetchEmployees();
    fetchShiftTypes();
  }, [currentWeekStart, currentView]);
  
  // Handle add new shift
  const handleAddShift = (date) => {
    resetShiftForm();
    
    setCurrentShift({
      ...currentShift,
      date: date
    });
    
    setModalMode('add');
    setShowShiftModal(true);
  };

  // Handle creating a shift from availability
  const handleCreateShiftFromAvailability = (shiftData) => {
    try {
      // Convert to the format expected by the form
      const formattedShift = {
        date: shiftData.date,
        employeeId: shiftData.employeeId.toString(),
        shiftTypeId: shiftData.shiftTypeId.toString(),
        startTime: minutesToTimeString(shiftData.startTime),
        endTime: minutesToTimeString(shiftData.endTime),
        isCustomTime: shiftData.isCustomTime || false,
        notes: shiftData.notes || ''
      };
      
      // Set form data
      setCurrentShift(formattedShift);
      
      // Show modal for confirmation/editing
      setModalMode('add');
      setShowShiftModal(true);
    } catch (err) {
      console.error('Error preparing shift from availability:', err);
      setError('Vardiya bilgileri hazırlanırken bir hata oluştu');
    }
  };
  
  // Reset the shift form
  const resetShiftForm = () => {
    setCurrentShift({
      date: '',
      employeeId: '',
      shiftTypeId: '',
      startTime: '',
      endTime: '',
      isCustomTime: false,
      notes: ''
    });
  };
  
  // Handle shift type change
  const handleShiftTypeChange = (e) => {
    const shiftTypeId = e.target.value;
    
    if (shiftTypeId) {
      const selectedType = shiftTypes.find(type => type.id.toString() === shiftTypeId);
      
      if (selectedType) {
        if (!currentShift.isCustomTime) {
          // Özel saat aralığı seçili değilse vardiya türünün varsayılan saatlerini kullan
          setCurrentShift({
            ...currentShift,
            shiftTypeId,
            startTime: minutesToTimeString(selectedType.defaultStartTime),
            endTime: minutesToTimeString(selectedType.defaultEndTime)
          });
        } else {
          // Özel saat aralığı seçili ise sadece vardiya türünü güncelle
          setCurrentShift({
            ...currentShift,
            shiftTypeId
          });
        }
      }
    } else {
      // Vardiya türü seçilmediğinde
      setCurrentShift({
        ...currentShift,
        shiftTypeId: ''
      });
    }
  };
  
  // Handle input change for form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'isCustomTime') {
      if (checked) {
        // Özel saat aralığı seçildiğinde mevcut saatleri koru
        setCurrentShift({
          ...currentShift,
          isCustomTime: true
        });
      } else {
        // Özel saat aralığı kapatıldığında vardiya türünün varsayılan saatlerini kullan
        if (currentShift.shiftTypeId) {
          const selectedType = shiftTypes.find(type => type.id.toString() === currentShift.shiftTypeId);
          if (selectedType) {
            setCurrentShift({
              ...currentShift,
              isCustomTime: false,
              startTime: minutesToTimeString(selectedType.defaultStartTime),
              endTime: minutesToTimeString(selectedType.defaultEndTime)
            });
          } else {
            setCurrentShift({
              ...currentShift,
              isCustomTime: false
            });
          }
        } else {
          setCurrentShift({
            ...currentShift,
            isCustomTime: false
          });
        }
      }
    } else {
      setCurrentShift({
        ...currentShift,
        [name]: value
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert time strings to minutes
      const startTimeMinutes = timeStringToMinutes(currentShift.startTime);
      const endTimeMinutes = timeStringToMinutes(currentShift.endTime);
      
      const shiftData = {
        ...currentShift,
        startTime: startTimeMinutes,
        endTime: endTimeMinutes
      };
      
      if (modalMode === 'add') {
        await apiService.createShift(shiftData);
      } else {
        // Edit mode
        await apiService.updateShift(currentShift.id, shiftData);
      }
      
      // Close modal and refresh data
      setShowShiftModal(false);
      fetchWeeklyShifts();
    } catch (err) {
      console.error('Error saving shift:', err);
      setError('Vardiya kaydedilirken bir hata oluştu: ' + 
        (err.response?.data?.error || err.message));
    }
  };
  
  // Handle edit shift
  const handleEditShift = (shift) => {
    // Convert minutes to time strings
    const startTime = minutesToTimeString(shift.startTime);
    const endTime = minutesToTimeString(shift.endTime);
    
    setCurrentShift({
      id: shift.id,
      date: shift.date,
      employeeId: shift.employeeId.toString(),
      shiftTypeId: shift.shiftTypeId.toString(),
      startTime,
      endTime,
      isCustomTime: shift.isCustomTime,
      notes: shift.notes || ''
    });
    
    setModalMode('edit');
    setShowShiftModal(true);
  };
  
  // Handle delete shift
  const handleDeleteShift = async (id) => {
    if (!window.confirm('Bu vardiyayı silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await apiService.deleteShift(id);
      
      // Refresh data
      fetchWeeklyShifts();
    } catch (err) {
      console.error('Error deleting shift:', err);
      setError('Vardiya silinirken bir hata oluştu: ' + 
        (err.response?.data?.error || err.message));
    }
  };
  
  // Get employee full name
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.name} ${employee.surname}` : 'Bilinmeyen Çalışan';
  };
  
  // Get shift type details
  const getShiftTypeDetails = (shiftTypeId) => {
    const shiftType = shiftTypes.find(type => type.id === shiftTypeId);
    return shiftType || { name: 'Bilinmeyen Vardiya', color: '#999' };
  };
  
  // Check if user is admin
  const isAdmin = authService.getUser()?.role === 'admin';
  
  // Generate week dates
  const weekDates = getWeekDates();

  const getSortedShiftsForDate = (date) => {
    const dayShifts = Array.isArray(weeklyShifts?.[date]) ? weeklyShifts[date] : [];
    return [...dayShifts].sort((a, b) => {
      const aStart = Number(a?.startTime ?? 0);
      const bStart = Number(b?.startTime ?? 0);
      if (aStart !== bStart) return aStart - bStart;

      const nameA = getEmployeeName(a.employeeId);
      const nameB = getEmployeeName(b.employeeId);
      return nameA.localeCompare(nameB, 'tr', { sensitivity: 'base' });
    });
  };

  // Render the active component based on currentView
  const renderActiveView = () => {
    switch (currentView) {
      case 'weekly':
        return renderWeeklyView();
      case 'employees':
        return <EmployeeManagement />;
      case 'shifttypes':
        return <ShiftTypeManagement />;
      case 'availabilities':
        return <AvailabilityManagement onCreateShift={handleCreateShiftFromAvailability} />;
      case 'payments':
        return <EmployeePaymentManagement />;
      case 'checkins':
        return <CheckInManagement />;
      default:
        return renderWeeklyView();
    }
  };
  
  // Render Weekly View
  const renderWeeklyView = () => {
    return (
      <>
        <div className="calendar-controls">
          <div className="week-navigation">
            <button 
              onClick={getPreviousWeek} 
              title="Önceki Hafta"
              className="nav-button"
            >
              <span className="icon">«</span>
              <span className="nav-text">Önceki Hafta</span>
            </button>
            <button 
              onClick={goToCurrentWeek} 
              className="current-week-button"
            >
              Bu Hafta
            </button>
            <button 
              onClick={getNextWeek} 
              title="Sonraki Hafta"
              className="nav-button"
            >
              <span className="nav-text">Sonraki Hafta</span>
              <span className="icon">»</span>
            </button>
          </div>
          <div className="week-display">
            <h3>
              {fromYMD(currentWeekStart).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
              {' - '}
              {(() => {
                const end = fromYMD(currentWeekStart);
                end.setDate(end.getDate() + 6);
                return end.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
              })()}
            </h3>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Yükleniyor...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            <div className="weekly-calendar weekly-calendar--grid">
              <div className="calendar-header">
                {weekDates.map(day => (
                  <div key={day.date} className="day-header">
                    <div className="day-name">{day.dayName}</div>
                    <div className="day-date">{day.displayDate}</div>
                  </div>
                ))}
              </div>
              
              <div className="calendar-body">
                {weekDates.map(day => (
                  <div key={day.date} className="day-column">
                    {isAdmin && (
                      <button 
                        className="add-shift-button"
                        onClick={() => handleAddShift(day.date)}
                        title="Bu Güne Vardiya Ekle"
                      >
                        <span className="icon">+</span>
                        <span className="button-text">Vardiya Ekle</span>
                      </button>
                    )}
                    
                    <div className="day-shifts">
                      {getSortedShiftsForDate(day.date).map(shift => {
                        const shiftType = getShiftTypeDetails(shift.shiftTypeId);
                        return (
                          <div 
                            key={shift.id} 
                            className="shift-card"
                            style={{ borderLeft: `4px solid ${shiftType.color}` }}
                            onClick={() => isAdmin && handleEditShift(shift)}
                          >
                            <div className="shift-header">
                              <span className="shift-type">{shiftType.name}</span>
                              {isAdmin && (
                                <button 
                                  className="delete-shift-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteShift(shift.id);
                                  }}
                                  title="Vardiyayı Sil"
                                >
                                  <span className="button-text">Sil</span>
                                  <span className="icon">×</span>
                                </button>
                              )}
                            </div>
                            <div className="shift-employee">
                              {getEmployeeName(shift.employeeId)}
                            </div>
                            <div className="shift-time">
                              {minutesToTimeString(shift.startTime)} - {minutesToTimeString(shift.endTime)}
                            </div>
                            {shift.notes && (
                              <div className="shift-notes">{shift.notes}</div>
                            )}
                          </div>
                        );
                      })}
                      
                      {getSortedShiftsForDate(day.date).length === 0 && (
                        <div className="no-shifts">Vardiya yok</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="weekly-list">
              {weekDates.map((day) => {
                const dayShifts = getSortedShiftsForDate(day.date);
                return (
                  <div key={day.date} className="day-card">
                    <div className="day-card-header">
                      <div className="day-card-title">
                        <div className="day-card-name">{day.dayName}</div>
                        <div className="day-card-date">{day.displayDate}</div>
                      </div>

                      {isAdmin && (
                        <button
                          className="add-shift-button add-shift-button--compact"
                          onClick={() => handleAddShift(day.date)}
                          title="Bu Güne Vardiya Ekle"
                        >
                          <span className="icon">+</span>
                          <span className="add-shift-label">Vardiya Ekle</span>
                        </button>
                      )}

                      <div className="day-card-count">{dayShifts.length}</div>
                    </div>

                    <div className="day-shifts">
                      {dayShifts.map((shift) => {
                        const shiftType = getShiftTypeDetails(shift.shiftTypeId);
                        return (
                          <div
                            key={shift.id}
                            className="shift-card"
                            style={{ borderLeft: `4px solid ${shiftType.color}` }}
                            onClick={() => isAdmin && handleEditShift(shift)}
                          >
                            <div className="shift-header">
                              <span className="shift-type">{shiftType.name}</span>
                              {isAdmin && (
                                <button
                                  className="delete-shift-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteShift(shift.id);
                                  }}
                                  title="Vardiyayı Sil"
                                >
                                  <span className="button-text">Sil</span>
                                  <span className="icon">×</span>
                                </button>
                              )}
                            </div>
                            <div className="shift-employee">{getEmployeeName(shift.employeeId)}</div>
                            <div className="shift-time">
                              {minutesToTimeString(shift.startTime)} - {minutesToTimeString(shift.endTime)}
                            </div>
                            {shift.notes && <div className="shift-notes">{shift.notes}</div>}
                          </div>
                        );
                      })}

                      {dayShifts.length === 0 && <div className="no-shifts">Vardiya yok</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="shift-management">
      <div className="section-header">
        <h2>Mesai Yönetimi</h2>
        <div className="view-toggle">
          <button 
            className={currentView === 'weekly' ? 'active' : ''}
            onClick={() => setCurrentView('weekly')}
          >
            Haftalık Görünüm
          </button>
          <button 
            className={currentView === 'employees' ? 'active' : ''}
            onClick={() => setCurrentView('employees')}
          >
            Personel Yönetimi
          </button>
          <button 
            className={currentView === 'shifttypes' ? 'active' : ''}
            onClick={() => setCurrentView('shifttypes')}
          >
            Vardiya Türleri
          </button>
          <button 
            className={currentView === 'availabilities' ? 'active' : ''}
            onClick={() => setCurrentView('availabilities')}
          >
            Personel Uygunlukları
          </button>
          <button 
            className={currentView === 'payments' ? 'active' : ''}
            onClick={() => setCurrentView('payments')}
          >
            Personel Ödemeleri
          </button>
          <button 
            className={currentView === 'checkins' ? 'active' : ''}
            onClick={() => setCurrentView('checkins')}
          >
            Giriş Çıkışlar
          </button>
        </div>
      </div>
      
      {renderActiveView()}
      
      {/* Add/Edit Shift Modal */}
      {showShiftModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{modalMode === 'add' ? 'Yeni Vardiya Ekle' : 'Vardiya Düzenle'}</h3>
              <button 
                className="close-button"
                onClick={() => setShowShiftModal(false)}
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="date">Tarih</label>
                  <input 
                    type="date" 
                    id="date" 
                    name="date" 
                    value={currentShift.date} 
                    onChange={handleInputChange} 
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="employeeId">Çalışan *</label>
                  <select 
                    id="employeeId" 
                    name="employeeId" 
                    value={currentShift.employeeId} 
                    onChange={handleInputChange} 
                    required
                  >
                    <option value="">Çalışan Seçin</option>
                    {employees.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name} {employee.surname} ({employee.position})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="shiftTypeId">Vardiya Türü *</label>
                  <select 
                    id="shiftTypeId" 
                    name="shiftTypeId" 
                    value={currentShift.shiftTypeId} 
                    onChange={handleShiftTypeChange} 
                    required
                  >
                    <option value="">Vardiya Türü Seçin</option>
                    {shiftTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} ({minutesToTimeString(type.defaultStartTime)} - {minutesToTimeString(type.defaultEndTime)})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input 
                      type="checkbox" 
                      name="isCustomTime" 
                      checked={currentShift.isCustomTime} 
                      onChange={handleInputChange} 
                    />
                    Özel Saat Aralığı
                  </label>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startTime">Başlangıç Saati *</label>
                  <input 
                    type="time" 
                    id="startTime" 
                    name="startTime" 
                    value={currentShift.startTime} 
                    onChange={handleInputChange} 
                    required 
                    disabled={!currentShift.isCustomTime && currentShift.shiftTypeId !== ''}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="endTime">Bitiş Saati *</label>
                  <input 
                    type="time" 
                    id="endTime" 
                    name="endTime" 
                    value={currentShift.endTime} 
                    onChange={handleInputChange} 
                    required 
                    disabled={!currentShift.isCustomTime && currentShift.shiftTypeId !== ''}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="notes">Notlar</label>
                <textarea 
                  id="notes" 
                  name="notes" 
                  value={currentShift.notes || ''} 
                  onChange={handleInputChange} 
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setShowShiftModal(false)}
                >
                  İptal
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftManagement;
